import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useNavigate, useParams } from 'react-router-dom';
import api from "api/axiosConfig";
import API_BASE from "configs/hostConfig";
import { useFormik } from 'formik';
import {
  Box,
  Button,
  Heading,
  Flex,
  IconButton,
  Text,
  CheckboxGroup,
  HStack,
  VStack,
  Checkbox,
  useToast,
  Container,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  useDisclosure,
  useBreakpointValue,
} from '@chakra-ui/react';
import { ChevronLeftIcon, ChevronRightIcon } from '@chakra-ui/icons';
import { TbAlertCircle } from 'react-icons/tb';
import PromptCard from './components/PromptCard';

import theme from 'ui/theme/theme';
import TextField from 'ui/InputField/TextField';
import TextArea from 'ui/TextArea/TextArea';
import CommentCard from './components/CommentCard';
import InactiveBanner from './components/InactiveBanner';
import ReviseVersion from './ReviseVersion';
import SmallScreenAlert from './components/SmallScreenAlert';

function RevisingForm({
  formId,
  breadcrumbProg,
  setBreadcrumbProg,
  nextPath,
  returnUrl,
  isPlanActive,
}) {
  // console.log('---------------entering revision page');

  const { essayId } = useParams();
  const navigate = useNavigate();
  const [revisionData, setRevisionData] = useState({});
  const [feedbackTasks, setFeedbackTasks] = useState([]);
  const [version, setVersion] = useState(0);
  // const [exerciseData, setExerciseData] = useState({});
  const [promptData, setPromptData] = useState({});
  const [enableNext, setEnableNext] = useState(false);
  const isNarrow = useBreakpointValue({ base: true, md: true, lg: false });
  const [btnSubmitType, setBtnSubmitType] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [revisionContent, setRevisionContent] = useState('');
  const [wordCount, setWordCount] = useState(0);

  const [selectedVersion, setSelectedVersion] = useState(null);
  const [selectedVersionData, setSelectedVersionData] = useState({});
  const { isOpen, onOpen, onClose } = useDisclosure();

  const toast = useToast();

  const handleWordCount = (text) => {
    const count = text?.trim() ? text.trim().split(/\s+/).length : 0;
    setWordCount(count);
  };

  function updateStateData(details) {
    const latestRevision = details?.latestRevision;
    setRevisionData(latestRevision);
    setFeedbackTasks(latestRevision?.feedbackTasks);
    setRevisionContent(latestRevision?.content);
    setVersion(latestRevision?.version);
    setPromptData({
      essayTitle: details.essayTitle,
      essayType: details.secondaryType || details.primaryType,
      essayPrompt: details.prompt,
      url: details.illustration,
      level: details.level,
      minWordCount: details.minWordCount,
    });
    setEnableNext(details.progress > 4 ? true : false);
    handleWordCount(latestRevision?.content);
  }

  useEffect(() => {
    setBreadcrumbProg(breadcrumbProg);
  }, [setBreadcrumbProg, breadcrumbProg]);

  useEffect(() => {
    refreshRevisions();
  }, []);

  const refreshRevisions = () => {
    // console.log('========= refresh revision data');
    api
      .get(
        `${API_BASE}/api/essay-exercise/data-with-latest-revision?exerciseId=${essayId}`
      )
      .then((res) => {
        if (res.status == 200) {
          const essayDetails = res.data?.data;
          // console.log('essay details after refreshRevisions: ', essayDetails);
          updateStateData(essayDetails);
          localStorage.setItem(
            'essayExerciseDetails',
            JSON.stringify(essayDetails)
          );
        }
      })
      .catch(() =>
        console.log('failed to fetch latest revision data, essay id: ', essayId)
      );
  };

  const handleCheckboxChanged = (checked, idx) => {
    let tasks = [];
    feedbackTasks?.map((task, i) => {
      if (i == idx) {
        tasks.push({ task: task.task, checked: checked });
      } else {
        tasks.push(task);
      }
    });
    formik.setFieldValue('tasks', tasks);
  };

  const validate = async (values) => {
    const errors = {};
    if (!values.title) {
      errors.title = 'Please take a moment to give your work a great title!';
    }
    return errors;
  };

  const formik = useFormik({
    validateOnChange: false,
    enableReinitialize: true,
    initialValues: {
      ...{
        exerciseId: essayId,
        title: revisionData?.title,
        draft: revisionContent,
        tasks: feedbackTasks,
      },
    },
    onSubmit: async (values) => {
      // console.log('question details form values: ', values);
      // console.log('submit state: ', btnSubmitType);
      if (btnSubmitType !== 0 && !isTaskFinished()) {
        return;
      }
      setIsSubmitting(true);
      updateRevison(values, btnSubmitType);
    },
    validate,
  });

  useEffect(() => {
    setFeedbackTasks(formik.values.tasks);
  }, [formik.values.tasks]);

  function updateRevison(values, updateType) {
    // console.log('+++to update revisions: ', values);
    if ((!values?.draft || values.draft.length < 20) && updateType > 0) {
      const warning =
        'Your submission is a bit brief. We know you have great ideas to share!';
      toastMessage(warning);
      return;
    }
    api
      .patch(`${API_BASE}/api/essay-exercise/revisions`, {
        ...values,
        updateType: updateType,
      })
      .then((res) => {
        // console.log('=======patch revisions response: ', res);
        if (res.status == 200) {
          const updatedEssayData = res.data?.data;
          localStorage.setItem(
            'essayExerciseDetails',
            JSON.stringify(updatedEssayData)
          );
          if (updateType === 2) {
            navigate(nextPath);
          } else if (updateType === 1) {
            updateStateData(updatedEssayData);
          }
        }
      })
      .catch(() => alert('Failed to update revision.'))
      .finally(() => setIsSubmitting(false));
  }

  const isTaskFinished = () => {
    if (!Array.isArray(feedbackTasks) || feedbackTasks.length == 0) {
      return true;
    }
    for (let i = 0; i < feedbackTasks.length; i++) {
      if (!feedbackTasks[i]?.checked) {
        return false;
      }
    }
    return true;
  };

  const handleRevisionClick = (revision) => {
    setSelectedVersion(revision);
    api
      .get(
        `${API_BASE}/api/essay-exercise/revision-version?exerciseId=${essayId}&version=${revision}`
      )
      .then((res) => {
        if (res.status == 200) {
          setSelectedVersionData(res.data.data);
          onOpen();
        }
      })
      .catch(() =>
        console.log(
          'failed to fetch version data, essay id: ',
          essayId,
          'version: ',
          revision
        )
      );
  };

  function toastMessage(msg) {
    toast({
      position: 'top',
      render: () => (
        <Box
          border="1px solid"
          // color={theme.colors.semantics['warning-light']}
          borderColor={theme.colors.semantics.warning}
          p={4}
          bg={theme.colors.semantics['warning-light']}
          borderRadius={12}
          wordBreak="break-all"
        >
          <Flex>
            <TbAlertCircle color={theme.colors.semantics.warning} />
            <Flex ml="12px" flex="1">
              <Text fontWeight="bold" ml="2">
                {msg}
              </Text>
            </Flex>
          </Flex>
        </Box>
      ),
      duration: 3000,
    });
  }

  if (isNarrow) {
    return <SmallScreenAlert />;
  }

  return (
    <Container width={isCollapsed ? '75%' : '100%'} maxW="none">
      {!isPlanActive && <InactiveBanner />}
      <Flex height="calc(100vh - 48px)" overflow="hidden">
        {/* Left Panel */}
        <Box
          flex="2"
          overflowY="auto" // Independent scroll for this section
          pl={4}
          pt={2}
          // pb={4}
          borderRight="1px solid #e0e0e0"
        // bg="gray.50"
        >
          <Flex flexDirection="column" marginBottom="16px">
            <PromptCard
              promptData={promptData}
              defaultShow={false}
              labelShift={true}
            />
            <Box bg="gray.50" pl={4} pt={2} pb={4}>
              <form id={formId} onSubmit={formik.handleSubmit}>
                <Flex flexDirection="column">
                  <Heading size="md" color="teal.700" mb="2px">
                    Revising
                  </Heading>
                  <TextField
                    id="title"
                    // aria-label="Title"
                    type="text"
                    placeholder="Essay title"
                    fontSize="1.2em"
                    fontWeight="bold"
                    errorMessage={formik.touched.title && formik.errors.title}
                    handleChange={formik.handleChange}
                    handleBlur={formik.handleBlur}
                    value={formik.values?.title}
                    maxLength="200"
                    marginBottom="0px"
                    minHeight="32px"
                  />
                  <TextArea
                    id="draft"
                    type="textarea"
                    placeholder="Write your essay here"
                    fontSize="1.2em"
                    errorMessage={formik.touched.draft && formik.errors.draft}
                    handleChange={(e) => {
                      formik.handleChange(e);
                      handleWordCount(e.target.value);
                    }}
                    handleBlur={formik.handleBlur}
                    value={formik.values?.draft}
                    width="100%"
                    inputRows={100}
                    inputHeight="26em"
                    marginBottom="0px"
                  />
                </Flex>
                {/* <Text>{JSON.stringify(formik.values)}</Text> */}
              </form>
              <Box mt={2}>
                <Flex align="center" justify="space-between" w="100%">
                  <Text color="gray.500">Word Count: {wordCount}</Text>
                  <Flex gap={2} mr={4}>
                    <Button
                      colorScheme="gray"
                      type="button"
                      variant="link"
                      onClick={() => navigate(returnUrl)}
                    >
                      Back
                    </Button>
                    {enableNext ? (
                      <Button
                        colorScheme="green"
                        type="button"
                        onClick={() => navigate(nextPath)}
                      >
                        Next
                      </Button>
                    ) : (
                      <>
                        <Button
                          form={formId}
                          colorScheme="green"
                          type="submit"
                          variant="outline"
                          isLoading={isSubmitting}
                          isDisabled={isSubmitting}
                          onClick={() => {
                            setBtnSubmitType(0);
                            formik.handleSubmit;
                          }}
                        >
                          Save for later
                        </Button>
                        <Button
                          form={formId}
                          colorScheme="green"
                          type="submit"
                          variant="outline"
                          isLoading={isSubmitting}
                          isDisabled={
                            isSubmitting ||
                            !isTaskFinished() ||
                            version >= 5 ||
                            !isPlanActive
                          }
                          onClick={() => {
                            setBtnSubmitType(1);
                            formik.handleSubmit;
                          }}
                        >
                          Evaluate
                        </Button>
                        <Button
                          form={formId}
                          colorScheme="green"
                          type="submit"
                          isLoading={isSubmitting}
                          isDisabled={
                            isSubmitting || !isTaskFinished() || !isPlanActive
                          }
                          onClick={() => {
                            setBtnSubmitType(2);
                            formik.handleSubmit;
                          }}
                        >
                          Submit
                        </Button>
                      </>
                    )}
                  </Flex>
                </Flex>
              </Box>
            </Box>
          </Flex>
        </Box>
        {/* Collapse/Expand Button */}
        <IconButton
          mt="16px"
          aria-label={isCollapsed ? 'Expand' : 'Collapse'}
          icon={isCollapsed ? <ChevronLeftIcon /> : <ChevronRightIcon />}
          onClick={() => setIsCollapsed(!isCollapsed)}
          fontSize="24px"
          boxSize="28px"
          alignSelf="start"
          background="transparent"
        />
        {/* Collapsible Right Panel */}
        <Box
          // width={isCollapsed ? '0' : '40%'}
          flex={isCollapsed ? '0' : '1'}
          // overflow="hidden"
          ml={-4}
          mt={2}
          pr={2}
          pb={4}
          overflowY="auto"
          transition="min-width 0.3s ease-in-out"
        >
          {!isCollapsed && (
            <Flex direction="column">
              <CommentCard comment={revisionData?.comment} defaultShow={true} />

              {feedbackTasks && (
                <>
                  <Heading size="md" color="teal.700" mb="4px">
                    Revising Tasks
                  </Heading>
                  <VStack p={2} alignItems="flex-start">
                    <CheckboxGroup colorScheme="green">
                      {feedbackTasks.map((task, idx) => (
                        <Flex
                          key={idx}
                          display="flex"
                          flexDirection="row"
                          alignItems="center"
                          gridColumnGap={2}
                          bg="green.50"
                          border="1px solid"
                          borderColor="green.500"
                          rounded="lg"
                          px={3}
                          py={1}
                        >
                          <Checkbox
                            // value={idx}
                            isChecked={task.checked}
                            onChange={(e) =>
                              handleCheckboxChanged(e.target.checked, idx)
                            }
                          >
                            {task.task}
                          </Checkbox>
                        </Flex>
                      ))}
                    </CheckboxGroup>
                  </VStack>
                </>
              )}
              {version > 1 && (
                <Box>
                  <Heading size="md" color="teal.700" mb="4px">
                    {version > 2 ? 'Previous Versions' : 'Previous Version'}
                  </Heading>
                  <HStack spacing={4} ml={2}>
                    {Array.from({ length: version - 1 }, (_, index) => (
                      <Text
                        key={index}
                        cursor="pointer"
                        color="blue.500"
                        fontWeight="bold"
                        onClick={() => handleRevisionClick(index + 1)}
                        _hover={{ textDecoration: 'underline' }}
                      >
                        Version {index + 1}
                      </Text>
                    ))}
                  </HStack>

                  <Modal isOpen={isOpen} onClose={onClose}>
                    <ModalOverlay />
                    <ModalContent maxWidth="700px">
                      <ModalHeader fontSize="md">
                        Version {selectedVersion}
                      </ModalHeader>
                      <ModalCloseButton />
                      <ModalBody>
                        {selectedVersion !== null && (
                          <ReviseVersion versionData={selectedVersionData} />
                        )}
                      </ModalBody>
                      <ModalFooter>
                        <Button colorScheme="green" onClick={onClose}>
                          Close
                        </Button>
                      </ModalFooter>
                    </ModalContent>
                  </Modal>
                </Box>
              )}
            </Flex>
          )}
        </Box>
        {/* </HStack> */}
      </Flex>
    </Container>
  );
}

RevisingForm.propTypes = {
  formId: PropTypes.string,
  breadcrumbProg: PropTypes.number,
  setBreadcrumbProg: PropTypes.func,
  nextPath: PropTypes.string,
  returnUrl: PropTypes.string,
  isPlanActive: PropTypes.bool,
};

export default RevisingForm;
